import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { Button, TextField, makeStyles, useTheme } from "@seahorse/ui";
import html2pdf from "html2pdf.js";
import {
  FaCopy as _FaCopy,
  FaDownload as _FaDownload,
  FaRedo as _FaRedo,
} from "react-icons/fa";
import { RenderFromString } from "../CoTraderChatTileComponent";
import { getAuthenticatedWebSocketUrl } from "../../Utils/CoTraderUtils";
import { createBestxSummaryHtmlStyles, mdToHtml } from "../BestX/bestxSummaryFormatting";
import ChatFinePrint from "../../../../Components/Common/ChatFinePrint";
import { useResponsiveTileTypography } from "../../Utils/useResponsiveTileTypography";

const FaCopy = _FaCopy as unknown as FC<any>;
const FaDownload = _FaDownload as unknown as FC<any>;
const FaRedo = _FaRedo as unknown as FC<any>;

interface TradeAnalysisConversationTileComponentProps {
  currencyPair: string;
  tradeType: "BUY" | "SELL";
  executionTimestamp: string;
  executionSpotRate?: number | string | null;
  executionForwardPoints?: number | string | null;
  expirationTimestamp?: string | null;
  userLanguage: string;
}

type ChatMessage = {
  id: string;
  type: "question" | "answer";
  text: string;
  backendMessageId?: string;
  variant?: "text" | "summary" | "report";
};

const createTradeAnalysisConversationId = () => {
  const userId = localStorage.getItem("userId") || "anonymous";
  const randomSuffix = Math.floor(100000 + Math.random() * 900000);
  return `BESTX-GPT-CONV-${userId}-${Date.now()}-${randomSuffix}`;
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const toPlainText = (htmlString: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  return doc.body.textContent || "";
};

const formatUrlList = (rawUrls: string) => {
  const urls = rawUrls.split(/\s+/).filter(Boolean);
  if (urls.length === 0) return "";

  const links = urls
    .map((url) => {
      const safeUrl = escapeHtml(url);
      return `<a href="${safeUrl}" target="_blank" rel="noreferrer">${safeUrl}</a>`;
    })
    .join("<br />");

  return `<div><strong>Sources</strong><br />${links}</div>`;
};

const hasHtmlTags = (value: string) => /<\/?[a-z][\s\S]*>/i.test(value);

const wrapSpecialPayload = (title: string, html: string) =>
  `<section><h3>${escapeHtml(title)}</h3>${html}</section>`;

const appendSpecialPayload = (
  current: ChatMessage,
  variant: "summary" | "report",
  title: string,
  payload: string
) => {
  if (current.variant === variant) {
    return {
      ...current,
      text: `${current.text}${payload}`,
    };
  }

  if (hasHtmlTags(payload)) {
    return {
      ...current,
      variant,
      text: `${current.text}${wrapSpecialPayload(title, payload)}`,
    };
  }

  return {
    ...current,
    variant,
    text: `${current.text}### ${title}\n\n${payload}`,
  };
};

const normalizeNumber = (value: number | string | null | undefined) => {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : value;
};

const buildSuggestions = (currencyPair: string, tradeType: string) => [
  `Analyze this ${tradeType} ${currencyPair} execution and explain what moved the market in the execution window.`,
  `Summarize the main risks and drivers for this ${tradeType} ${currencyPair} trade.`,
  "Ignore lower-confidence sources and focus on higher-quality market evidence.",
  "Generate the final report.",
];

const TradeAnalysisConversationTileComponent: React.FunctionComponent<
  TradeAnalysisConversationTileComponentProps
> = ({
  currencyPair,
  tradeType,
  executionTimestamp,
  executionSpotRate,
  executionForwardPoints,
  expirationTimestamp,
  userLanguage,
}) => {
  const theme = useTheme();
  const { containerRef, tier } =
    useResponsiveTileTypography<HTMLDivElement>();
  const colors = useMemo(
    () => ({
      dark: theme.globallink.colors.Grey[1100],
      light: theme.globallink.colors.White[500],
      border: theme.globallink.colors.White[500],
      accent: theme.globallink.colors.Blue[500],
      muted: theme.globallink.colors.Grey[200],
      surface: "rgba(255,255,255,0.06)",
      reportSurface: "#2A3342",
      reportText: "#E8EDF4",
      reportBorder: "#3B4657",
    }),
    [theme]
  );

  const typography = useMemo(() => {
    switch (tier) {
      case "compact":
        return {
          containerPadding: "8px",
          bodyPadding: "8px",
          bodyGap: "8px",
          titleSize: "13px",
          subtitleSize: "11px",
          introSize: "11px",
          messageSize: "12px",
          messageLineHeight: 1.5,
          messagePadding: "8px 10px",
          progressSize: "11px",
          suggestionSize: "11px",
          suggestionMinHeight: "52px",
          finePrintSize: "9px",
          finePrintLineHeight: "13px",
        };
      case "comfortable":
        return {
          containerPadding: "12px",
          bodyPadding: "12px",
          bodyGap: "12px",
          titleSize: "15px",
          subtitleSize: "13px",
          introSize: "13px",
          messageSize: "14px",
          messageLineHeight: 1.6,
          messagePadding: "12px 14px",
          progressSize: "13px",
          suggestionSize: "13px",
          suggestionMinHeight: "60px",
          finePrintSize: "10.5px",
          finePrintLineHeight: "15px",
        };
      default:
        return {
          containerPadding: "10px",
          bodyPadding: "10px",
          bodyGap: "10px",
          titleSize: "14px",
          subtitleSize: "12px",
          introSize: "12px",
          messageSize: "13px",
          messageLineHeight: 1.55,
          messagePadding: "10px 12px",
          progressSize: "12px",
          suggestionSize: "12px",
          suggestionMinHeight: "56px",
          finePrintSize: "10px",
          finePrintLineHeight: "14px",
        };
    }
  }, [tier]);
  const summaryHtmlStyles = useMemo(
    () => createBestxSummaryHtmlStyles(tier),
    [tier]
  );

  const useStyles = makeStyles({
    container: {
      width: "100%",
      height: "100%",
      display: "flex",
      marginTop: "10px",
      marginBottom: "10px",
      flexDirection: "column",
      borderRadius: "12px",
      padding: "10px",
      border: "1px solid rgba(255,255,255,0.14)",
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))",
    },
    headerRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "12px",
      padding: "6px 6px 10px 6px",
      flexWrap: "wrap",
    },
    headerLeft: {
      display: "flex",
      flexDirection: "column",
      gap: "2px",
      minWidth: 0,
    },
    title: {
      color: colors.light,
      fontSize: "14px",
      fontWeight: 700,
      lineHeight: "18px",
      margin: 0,
    },
    subtitle: {
      color: colors.muted,
      fontSize: "12px",
      margin: 0,
      opacity: 0.9,
    },
    headerActions: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      flexWrap: "wrap",
    },
    body: {
      overflow: "auto",
      height: "100%",
      width: "100%",
      backgroundColor: "transparent",
      padding: "10px",
      display: "flex",
      flexDirection: "column",
      borderRadius: "12px",
      gap: "10px",
    },
    introCard: {
      backgroundColor: colors.surface,
      border: "1px solid rgba(255,255,255,0.1)",
      borderRadius: "12px",
      color: colors.light,
      padding: "12px",
      fontSize: "12px",
      lineHeight: 1.5,
    },
    progressCard: {
      backgroundColor: "rgba(255,255,255,0.08)",
      borderRadius: "12px",
      color: colors.light,
      fontSize: "12px",
      lineHeight: 1.5,
      padding: "10px 12px",
    },
    message: {
      padding: "10px 12px",
      borderRadius: "12px",
      width: "fit-content",
      maxWidth: "100%",
    },
    question: {
      alignSelf: "flex-end",
      marginLeft: "auto",
      maxWidth: "80%",
      backgroundColor: colors.accent,
      color: "white",
    },
    answer: {
      alignSelf: "flex-start",
      marginRight: "auto",
      width: "100%",
      backgroundColor: colors.reportSurface,
      color: colors.reportText,
      border: `1px solid ${colors.reportBorder}`,
      boxShadow: "0 6px 18px rgba(0, 0, 0, 0.12)",
    },
    messageActions: {
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      gap: "10px",
      marginTop: "10px",
    },
    actionIcon: {
      cursor: "pointer",
      opacity: 0.8,
      transition: "all 0.15s ease",
      "&:hover": {
        opacity: 1,
        transform: "translateY(-1px)",
      },
    },
    summaryHtml: {
      ...summaryHtmlStyles,
      color: colors.reportText,
      "& code": {
        background: "rgba(255,255,255,0.08)",
        color: colors.reportText,
      },
    },
    suggestionGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: "8px",
      "@media (max-width: 720px)": {
        gridTemplateColumns: "minmax(0, 1fr)",
      },
    },
    suggestionButton: {
      width: "100%",
      textAlign: "left",
      justifyContent: "flex-start",
      lineHeight: 1.35,
      minHeight: "56px",
      whiteSpace: "normal",
    },
    composer: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: "10px",
      marginTop: "10px",
      "@media (max-width: 720px)": {
        flexDirection: "column",
        alignItems: "stretch",
      },
    },
    inputWrap: {
      flex: 1,
      minWidth: 0,
    },
    loadingDots: {
      display: "inline-block",
      "& span": {
        display: "inline-block",
        width: "8px",
        height: "8px",
        marginRight: "4px",
        backgroundColor: colors.light,
        borderRadius: "50%",
        animation: "$blink 1.4s infinite both",
      },
      "& span:nth-child(2)": {
        animationDelay: "0.2s",
      },
      "& span:nth-child(3)": {
        animationDelay: "0.4s",
      },
    },
    "@keyframes blink": {
      "0%, 80%, 100%": {
        opacity: 0,
      },
      "40%": {
        opacity: 1,
      },
    },
  });

  const classes = useStyles();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messageInput, setMessageInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [progressMessage, setProgressMessage] = useState("");
  const [sessionNotice, setSessionNotice] = useState(
    "Trade Analysis conversation is available here. Enter your query to begin."
  );
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const chatIdRef = useRef(createTradeAnalysisConversationId());
  const isFirstTradeStateRef = useRef(true);

  const suggestions = useMemo(
    () => buildSuggestions(currencyPair, tradeType),
    [currencyPair, tradeType]
  );

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, loading, progressMessage]);

  useEffect(() => {
    return () => {
      try {
        socketRef.current?.close();
      } catch {}
    };
  }, []);

  useEffect(() => {
    if (isFirstTradeStateRef.current) {
      isFirstTradeStateRef.current = false;
      return;
    }

    try {
      socketRef.current?.close();
    } catch {}

    socketRef.current = null;
    chatIdRef.current = createTradeAnalysisConversationId();
    setMessages([]);
    setLoading(false);
    setProgressMessage("");
    setSessionNotice(
      "Trade details changed, so a fresh Trade Analysis chat session is ready for the new context."
    );
  }, [
    currencyPair,
    tradeType,
    executionTimestamp,
    executionSpotRate,
    executionForwardPoints,
    expirationTimestamp,
  ]);

  const updateAnswerMessage = (
    messageId: string,
    updater: (message: ChatMessage) => ChatMessage
  ) => {
    setMessages((prev) =>
      prev.map((message) =>
        message.id === messageId ? updater(message) : message
      )
    );
  };

  const resetConversation = () => {
    try {
      socketRef.current?.close();
    } catch {}

    socketRef.current = null;
    chatIdRef.current = createTradeAnalysisConversationId();
    setMessages([]);
    setLoading(false);
    setProgressMessage("");
    setMessageInput("");
    setSessionNotice("Started a fresh Trade Analysis conversation session.");
  };

  const copyMessage = async (htmlString: string) => {
    try {
      await navigator.clipboard.writeText(toPlainText(htmlString));
      alert("Copied to clipboard!");
    } catch (error) {
      console.error("Copy failed:", error);
    }
  };

  const downloadMessage = async (htmlString: string) => {
    const container = document.createElement("div");
    container.innerHTML = htmlString;
    container.style.padding = "24px";
    container.style.fontFamily = "Arial, sans-serif";
    container.style.fontSize = "12px";
    document.body.appendChild(container);

    try {
      await html2pdf()
        .set({
          margin: 0.5,
          filename: `trade-analysis-report-${currencyPair.toLowerCase()}.pdf`,
          html2canvas: { scale: 2 },
          jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
        })
        .from(container)
        .save();
    } catch (error) {
      console.error("PDF export failed:", error);
    } finally {
      document.body.removeChild(container);
    }
  };

  const getFormattedAnswerHtml = (text: string) => {
    if (!text) return "";
    if (hasHtmlTags(text)) return text;
    return mdToHtml(text);
  };

  const handleSendMessage = async (rawInput?: string) => {
    if (loading) return;

    const userInput = (rawInput ?? messageInput).trim();
    if (!userInput) return;

    const apiUrl = process.env.REACT_APP_API_URL;
    const userId = localStorage.getItem("userId") || "anonymous";
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

    if (!apiUrl) {
      setMessages((prev) => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          type: "answer",
          text: "<strong>Error:</strong> Missing <code>REACT_APP_API_URL</code> configuration.",
        },
      ]);
      return;
    }

    const questionId = `question-${Date.now()}`;
    const answerId = `answer-${Date.now()}`;
    const requestBody = {
      chat_id: chatIdRef.current,
      bestx_mode: "conversation_v1",
      origin: "BESTX",
      trade_type: tradeType,
      currency_pair: currencyPair ?? null,
      execution_timestamp: executionTimestamp ?? null,
      execution_timezone: timeZone,
      execution_spot_rate: normalizeNumber(executionSpotRate),
      execution_forward_points: normalizeNumber(executionForwardPoints),
      expiration_timestamp: expirationTimestamp ?? null,
      query: userInput,
      userTimeZone: timeZone,
      user_language: userLanguage,
      embedding_model: "text-embedding-ada-002",
    };

    try {
      socketRef.current?.close();
    } catch {}

    setSessionNotice("");
    setProgressMessage("");
    setLoading(true);
    setMessageInput("");
    setMessages((prev) => [
      ...prev,
      { id: questionId, type: "question", text: escapeHtml(userInput) },
      { id: answerId, type: "answer", text: "", variant: "text" },
    ]);

    let ws: WebSocket;
    try {
      const wsUrl = await getAuthenticatedWebSocketUrl(
        chatIdRef.current,
        userId
      );
      ws = new WebSocket(wsUrl);
    } catch (error) {
      console.error("Trade Analysis conversation auth error:", error);
      updateAnswerMessage(answerId, (current) => ({
        ...current,
        text:
          current.text ||
          "<strong>Error:</strong> Unable to authenticate the Trade Analysis conversation service.",
      }));
      setLoading(false);
      return;
    }
    socketRef.current = ws;

    const finishTurn = () => {
      setLoading(false);
      setProgressMessage("");
      if (socketRef.current === ws) {
        socketRef.current = null;
      }
      try {
        ws.close();
      } catch {}
    };

    ws.onopen = () => {
      ws.send(JSON.stringify(requestBody));
    };

    ws.onmessage = (event) => {
      let data: any;

      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }

      const type = data?.type;
      const message = data?.message;

      if (
        type === "FINISH" ||
        type === "DONE" ||
        type === "END" ||
        type === "COMPLETE"
      ) {
        finishTurn();
        return;
      }

      if (type === "MESSAGE_ID" && message != null) {
        updateAnswerMessage(answerId, (current) => ({
          ...current,
          backendMessageId: String(message),
        }));
        return;
      }

      if (type === "INFO") {
        setProgressMessage(String(message || ""));
        return;
      }

      if (type === "SUMMARY_UPDATE" || type === "BESTX_STATE") {
        return;
      }

      if (message == null) {
        return;
      }

      if (type === "urls") {
        updateAnswerMessage(answerId, (current) => ({
          ...current,
          text: `${current.text}${formatUrlList(String(message))}`,
        }));
        return;
      }

      if (type === "BESTX_SUMMARY_HTML") {
        updateAnswerMessage(answerId, (current) =>
          appendSpecialPayload(
            current,
            "summary",
            "On-Demand Summary",
            String(message)
          )
        );
        return;
      }

      if (type === "BESTX_REPORT_HTML") {
        updateAnswerMessage(answerId, (current) =>
          appendSpecialPayload(
            current,
            "report",
            "Final Report",
            String(message)
          )
        );
        return;
      }

      if (type === "ERROR") {
        updateAnswerMessage(answerId, (current) => ({
          ...current,
          text: `${current.text}<div><strong>Error:</strong> ${String(message)}</div>`,
        }));
        return;
      }

      updateAnswerMessage(answerId, (current) => ({
        ...current,
        text: `${current.text}${String(message)}`,
      }));
    };

    ws.onerror = (error) => {
      console.error("Trade Analysis conversation WebSocket error:", error);
      updateAnswerMessage(answerId, (current) => ({
        ...current,
        text:
          current.text ||
          "<strong>Error:</strong> Unable to connect to the Trade Analysis conversation service.",
      }));
      finishTurn();
    };

    ws.onclose = () => {
      if (socketRef.current === ws) {
        socketRef.current = null;
      }
      setLoading(false);
      setProgressMessage("");
    };
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div
      className={classes.container}
      ref={containerRef}
      style={{ padding: typography.containerPadding }}
    >
      <div className={classes.headerRow}>
        <div className={classes.headerLeft}>
          <p className={classes.title} style={{ fontSize: typography.titleSize }}>
            Trade Analysis Chat
          </p>
          <p
            className={classes.subtitle}
            style={{ fontSize: typography.subtitleSize }}
          >
            Conversation session with reusable trade context
          </p>
        </div>

        <div className={classes.headerActions}>
          <Button onClick={resetConversation}>
            <FaRedo className="me-2" />
            New Chat
          </Button>
        </div>
      </div>

      <div
        className={classes.body}
        style={{ padding: typography.bodyPadding, gap: typography.bodyGap }}
      >
        {sessionNotice && (
          <div
            className={classes.introCard}
            style={{ fontSize: typography.introSize }}
          >
            <RenderFromString htmlString={sessionNotice} />
          </div>
        )}

        {messages.length === 0 && (
          <div className={classes.suggestionGrid}>
            {suggestions.map((suggestion) => (
              <Button
                key={suggestion}
                className={classes.suggestionButton as any}
                onClick={() => handleSendMessage(suggestion)}
                style={{
                  fontSize: typography.suggestionSize,
                  minHeight: typography.suggestionMinHeight,
                }}
              >
                {suggestion}
              </Button>
            ))}
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`${classes.message} ${
              message.type === "question" ? classes.question : classes.answer
            }`}
            style={{
              fontSize: typography.messageSize,
              lineHeight: typography.messageLineHeight,
              padding: typography.messagePadding,
            }}
          >
            {message.type === "answer" ? (
              <div
                className={classes.summaryHtml}
                style={{
                  fontSize: typography.messageSize,
                  lineHeight: typography.messageLineHeight,
                }}
                dangerouslySetInnerHTML={{
                  __html: getFormattedAnswerHtml(message.text),
                }}
              />
            ) : (
              <RenderFromString htmlString={message.text} />
            )}

            {message.type === "answer" && message.text && (
              <div className={classes.messageActions}>
                <FaCopy
                  className={classes.actionIcon as any}
                  onClick={() => copyMessage(message.text)}
                  title="Copy Message"
                />
                {message.variant === "report" && (
                  <FaDownload
                    className={classes.actionIcon as any}
                    onClick={() => downloadMessage(message.text)}
                    title="Download Report"
                  />
                )}
              </div>
            )}
          </div>
        ))}

        {progressMessage && (
          <div
            className={classes.progressCard}
            style={{ fontSize: typography.progressSize }}
          >
            <RenderFromString htmlString={progressMessage} />
          </div>
        )}

        {loading && (
          <div className={classes.loadingDots}>
            <span />
            <span />
            <span />
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className={classes.composer}>
        <div className={classes.inputWrap}>
          <ChatFinePrint
            assistantName="Trade Analysis Chat"
            style={{
              marginBottom: "6px",
              fontSize: typography.finePrintSize,
              lineHeight: typography.finePrintLineHeight,
            }}
          />
          <TextField
            placeholder="Ask a follow-up about this trade"
            value={messageInput}
            id="tradeAnalysisConversationInput"
            onChange={(value) => setMessageInput(value)}
            onKeyDown={handleKeyDown}
            endAdornment={
              <div
                className="hover:text-info"
                onClick={() => handleSendMessage()}
                style={{ cursor: "pointer" }}
              >
                <i className="fa-solid fa-paper-plane me-2"></i>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
};

export default TradeAnalysisConversationTileComponent;
